'use client';

/**
 * StackedWordmark — two-line all-caps wordmark placeholder.
 *
 * Used as a default brand mark on white-labeled websites until a tenant
 * uploads a real logo asset. The top line is heavy (Archivo Black 900 with
 * tight letter-spacing, falling back to native bold sans), the bottom line
 * is wider-spaced semibold underneath. Matches
 * `pages/whitelabel.jsx` `StackedWordmark`.
 *
 * Font note: Archivo is a Google Font. The website portal (#36) registers
 * it via `next/font/google`; this component falls back to a system bold
 * stack when Archivo isn't loaded, so Storybook / dev surfaces render
 * cleanly without extra setup.
 */

import { Box } from '@mui/material';
import { type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { forwardRef } from 'react';

export type StackedWordmarkSize = 'sm' | 'md' | 'lg' | 'xl';

export interface StackedWordmarkProps {
  /** Top line (e.g. `COASTAL`). Rendered in display weight, tight tracking. */
  top: string;
  /** Bottom line (e.g. `FUNDING`). Rendered in semibold, wide tracking. */
  bottom: string;
  /**
   * Size token. Mirrors the whitelabel reference scale:
   *  - `sm` — header-tight (~14px top, 7.5px bottom)
   *  - `md` — default (22 / 11)
   *  - `lg` — hero rail (32 / 16)
   *  - `xl` — page-takeover hero (48 / 24)
   * @default 'md'
   */
  size?: StackedWordmarkSize;
  /**
   * Solid color for both lines. Pass a theme path (`text.primary`) or any
   * CSS-color string. Defaults to `currentColor` so the consuming context
   * (light or dark surface) drives the color.
   */
  color?: string;
  /** Optional aria-label override. Defaults to `${top} ${bottom}`. */
  ariaLabel?: string;
  sx?: SxProps<Theme>;
}

interface SizeConfig {
  topFontSize: string;
  bottomFontSize: string;
  topLetterSpacing: string;
  bottomLetterSpacing: string;
  gap: number;
}

// Scale tuned to match `whitelabel.jsx` StackedWordmark (lines 7-12)
// but expressed in rem so the wordmark inherits the consuming portal's
// root-size adjustments.
const SIZE_MAP: Record<StackedWordmarkSize, SizeConfig> = {
  sm: {
    topFontSize: '0.875rem', // ~14px
    bottomFontSize: '0.46875rem', // ~7.5px
    topLetterSpacing: '-0.02em',
    bottomLetterSpacing: '0.32em',
    gap: 0.125,
  },
  md: {
    topFontSize: '1.375rem', // ~22px
    bottomFontSize: '0.6875rem', // ~11px
    topLetterSpacing: '-0.025em',
    bottomLetterSpacing: '0.5em',
    gap: 0.25,
  },
  lg: {
    topFontSize: '2rem', // ~32px
    bottomFontSize: '1rem', // ~16px
    topLetterSpacing: '-0.03em',
    bottomLetterSpacing: '0.5em',
    gap: 0.375,
  },
  xl: {
    topFontSize: '3rem', // ~48px
    bottomFontSize: '1.5rem', // ~24px
    topLetterSpacing: '-0.04em',
    bottomLetterSpacing: '0.5em',
    gap: 0.625,
  },
};

/**
 * Stacked all-caps wordmark — bold top line over wide-tracked bottom line.
 */
export const StackedWordmark = forwardRef<HTMLDivElement, StackedWordmarkProps>(function StackedWordmark(
  { top, bottom, size = 'md', color = 'currentColor', ariaLabel, sx },
  ref,
) {
  const cfg = SIZE_MAP[size];

  return (
    <Box
      ref={ref}
      role="img"
      aria-label={ariaLabel ?? `${top} ${bottom}`}
      sx={[
        {
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          lineHeight: 1,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography
        component="span"
        sx={{
          // Archivo Black falls back to native bold/UI stack if not loaded.
          fontFamily:
            '"Archivo Black", "Archivo", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
          fontWeight: 900,
          color,
          fontSize: cfg.topFontSize,
          letterSpacing: cfg.topLetterSpacing,
          lineHeight: 1,
          textTransform: 'uppercase',
        }}
      >
        {top}
      </Typography>
      <Typography
        component="span"
        sx={{
          // Slightly lighter bottom line (still semibold/bold) for the
          // letter-spaced "FUNDING"-style track.
          fontFamily: '"Archivo", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
          fontWeight: 800,
          color,
          fontSize: cfg.bottomFontSize,
          letterSpacing: cfg.bottomLetterSpacing,
          lineHeight: 1,
          mt: cfg.gap,
          textTransform: 'uppercase',
        }}
      >
        {bottom}
      </Typography>
    </Box>
  );
});

StackedWordmark.displayName = 'StackedWordmark';

export default StackedWordmark;
